import { gql } from "@apollo/client";
import { toItemInput } from "lib/apollo/conversions";
import { BASKET_UPDATE_FRAGMENT } from "lib/apollo/fragments";
import { createRequestContext, getClient } from "lib/apolloClient";
import { setBasket } from "lib/basket";
import { dispatch } from "@shared/utils.events";
import { setNotifications } from "lib/notifications";
import { setTermsAndConditions } from "lib/termsAndConditions";
import { setUser, userVar } from "lib/user";
import type { BasketUpdateResultType } from "@shared/graphql.types";
import { removeNull } from "utils/removeNull";
import { withGlobalSpinner } from "./api";
import { getTrackingEventId } from "./utils/trackingEventId";

const EDIT_BASKET_ITEM = gql`
  mutation editBasketItem($listId: ID!, $version: Int!, $item: ItemInput!) {
    editBasketItem(listId: $listId, version: $version, item: $item) {
      ...BasketUpdateFragment
    }
  }
  ${BASKET_UPDATE_FRAGMENT}
`;

async function changeQuantityInner(
  id: string,
  quantity: number,
  locationType?: string
) {
  const user = userVar();
  const itemInBasket = user?.basket?.find((item) => item.id === id);

  if (!itemInBasket || quantity === itemInBasket.quantity)
    return Promise.reject();

  const variables = {
    listId: "",
    version: -1,
    item: toItemInput({ ...itemInBasket, quantity })
  };

  const result = await getClient().mutate<{
    editBasketItem: BasketUpdateResultType;
  }>({
    mutation: EDIT_BASKET_ITEM,
    variables,
    context: createRequestContext({
      sessionStrategy: "START_NEW_ANONYMOUS"
    })
  });

  const eventId = getTrackingEventId();

  if (result?.data?.editBasketItem) {
    const {
      items,
      discounts,
      pricing,
      includedDiscounts,
      rejectedDiscounts,
      notifications,
      hasOnlyVirtualProducts,
      deliveryMethod,
      deliveryMethodName,
      termsAndConditions,
      gwpModule,
      thresholds
    } = result.data.editBasketItem;

    setUser({
      basket: items,
      discounts,
      pricing,
      includedDiscounts,
      rejectedDiscounts,
      hasOnlyVirtualProducts: !!hasOnlyVirtualProducts,
      deliveryMethod,
      deliveryMethodName: removeNull(deliveryMethodName)
    });

    setBasket({ gwpModule, thresholds, items });

    setTermsAndConditions(termsAndConditions);

    setNotifications(notifications);

    if (itemInBasket.quantity < quantity) {
      // TODO: when editBasketItems mutation returns the `updateTracking`, implement the firing of the tracking similar to addBasketItems.ts
      dispatch("addToBag", {
        items: [
          toItemInput({
            ...itemInBasket,
            quantity: quantity - itemInBasket.quantity
          })
        ],
        locationType,
        event_id: eventId
      });
    } else {
      dispatch("removeFromBag", {
        items: [
          toItemInput({
            ...itemInBasket,
            quantity: itemInBasket.quantity - quantity
          })
        ],
        locationType,
        event_id: eventId
      });
    }
  }

  return !result?.errors;
}

export const changeQuantity = (
  id: string,
  quantity: number,
  locationType?: string,
  options?: { runInBackground?: boolean }
): Promise<boolean> => {
  if (options?.runInBackground) {
    return changeQuantityInner(id, quantity, locationType);
  }

  return withGlobalSpinner(() =>
    changeQuantityInner(id, quantity, locationType)
  )();
};
